import axios from 'axios';
import {useState, useEffect} from 'react';
import {useQuery} from 'react-query';
import {useNavigate, useParams} from 'react-router-dom';
import Header from '../../../shared/Header';
import {Box, Stack} from '../../../shared/layouts';
import DescriptionMessage from '../../../shared/DescriptionMessage';
import {useTheme} from '@emotion/react';
import {useMediaQuery} from '@dnhb/node_package';
import Placeholder from '../../../shared/Placeholder';
import DocumentRequestDetailCard from './DocumentRequestDetailCard';
import Loader from '../../../shared/Loader';
import BackButton from '../../../shared/BackButton';
import UploadModalWrapper from './uploadModal/UploadModalWrapper';
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../../hooks/useIconLibrary";

const DocumentRequestDetailPage = () => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isError, data: apiData = {}, refetch } = useQuery(
    ["documentRequests", requestId],
    async () => {
      const { data } = await axios(`/files/request/${requestId}/data`);
      setIsLoading(false);
      return data;
    }
  );

  useEffect(() => {
    let timer;

    const checkProcessingFiles = () => {
      if (apiData.processing_files_count > 0) {
        timer = setTimeout(() => {
          refetch().then(checkProcessingFiles);
        }, 5000);
      }
    };

    checkProcessingFiles();

    return () => clearTimeout(timer);
  }, [apiData.processing_files_count, refetch]);

  const { requestId } = useParams();
  const navigate = useNavigate();
  const { breakpoints } = useTheme();
  const isMinTablet = useMediaQuery(
    `(min-width: ${breakpoints.md ?? "37.5rem"})`
  );
  const isOpenModal = (openModal ?? false) !== false ? true : false;
  const data = apiData?.documents || [];
  const { open, resolved } = data.reduce(
    (final, current) => {
      const isRejected = current.status === 'rejected';
      if (current.documents_count === 0 || isRejected) {
        final.open.push({ ...current, isRejected });
        final.open.sort((a, b) => {
          if (a.isRejected) return -1;
          if (b.isRejected) return 1;
          return 0;
        });
      } else final.resolved.push(current);
      return final;
    },
    { open: [], resolved: [] }
  );

  const { DocumentsIcon } = useIconLibrary();

  const Content = () => {
    if (isError) {
      return (
        <Placeholder>
          {t('Er kunnen geen documentverzoeken opgehaald worden')}
        </Placeholder>
      );
    }

    return (
      <>
        <Box>
          <b>{t('Openstaand')}</b>
          <p css={{ margin: "12px 0", fontSize: { mobile: "15px", tablet: "14px" } }}>
            {t('Upload documenten voor de onderstaande documentverzoeken.')}
          </p>
          {open.length === 0 ? (
            <Placeholder>{t('Er zijn geen openstaande documenten')}</Placeholder>
          ) : (
            <Stack space="s">
              {open.map(data => (
                <DocumentRequestDetailCard
                  key={data.id}
                  {...{ ...data, setOpenModal, isMinTablet }}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Box>
          <b>{t('Geüpload')}</b>
          <p css={{ margin: "12px 0", fontSize: { mobile: "15px", tablet: "14px" } }}>
            {t('Voor deze documenten heb je al bestanden geüpload.')}
          </p>
          {resolved.length === 0 ? (
            <Placeholder>{t('Er zijn geen afgeronde documenten')}</Placeholder>
          ) : (
            <Stack space="s">
              {resolved.map(data => (
                <DocumentRequestDetailCard
                  isResolved
                  key={data.id}
                  {...{ ...data, setOpenModal, isMinTablet }}
                />
              ))}
            </Stack>
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      {isLoading  && <Loader />}
      <Stack space="xl" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        {requestId !== null && (
          <BackButton onClick={() => navigate('/requests')}>
            {t('Terug')}
          </BackButton>
        )}
        <Header
          title={t(`Documentverzoeken: {{name}}`, {name: apiData?.name ?? ""})}
          icon={<DocumentsIcon height="2rem" width="2rem" />}
          variant="page"
        />
        {apiData?.description && (
          <DescriptionMessage>{apiData.description}</DescriptionMessage>
        )}
        <Content />
        <UploadModalWrapper
          requestIsCompleted={apiData?.status === "completed"}
          isOpen={isOpenModal}
          onRequestClose={() => setOpenModal(null)}
          documentTypeId={openModal}
          documentData={apiData?.documents?.find((doc) => doc.id === openModal)}
          refetchPageData={refetch}
        />
      </Stack>
    </>
  );
};

export default DocumentRequestDetailPage;
